import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Calendar as CalendarIcon,
  Link as LinkIcon,
  Unlink,
  RefreshCw,
  Loader2,
  CheckCircle2,
  AlertCircle,
  ArrowDownUp,
  ArrowDown,
  ArrowUp,
  Power,
} from 'lucide-react';
import { toast } from 'sonner';

const PROVIDERS = [
  {
    key: 'google',
    name: 'Google Calendar',
    accent: 'text-red-600',
    bg: 'bg-red-50',
    border: 'border-red-200',
  },
  {
    key: 'outlook',
    name: 'Microsoft Outlook',
    accent: 'text-blue-600',
    bg: 'bg-blue-50',
    border: 'border-blue-200',
  },
];

const SYNC_MODE_OPTIONS = [
  { value: 'two_way', label: 'Synchronisation bidirectionnelle', icon: ArrowDownUp },
  { value: 'read_only', label: 'Importer uniquement (lecture)', icon: ArrowDown },
  { value: 'push_only', label: 'Pousser uniquement (écriture)', icon: ArrowUp },
  { value: 'off', label: 'Désactivé', icon: Power },
];

export const CalendarIntegrations = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState({ providers: { google: {}, outlook: {} } });
  const [busy, setBusy] = useState({});
  const [syncing, setSyncing] = useState({});

  const fetchStatus = useCallback(async () => {
    try {
      const res = await api.get('/calendar/status');
      setStatus(res.data);
    } catch (err) {
      console.error('calendar status error', err);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchStatus();
    // If we just returned from an OAuth flow, show feedback
    const params = new URLSearchParams(window.location.search);
    const calendar = params.get('calendar');
    const detail = params.get('detail');
    if (calendar === 'success') {
      toast.success(`Calendrier connecté${detail ? ` (${detail})` : ''}`);
      window.history.replaceState({}, '', window.location.pathname);
    } else if (calendar === 'error') {
      toast.error(`Connexion échouée${detail ? ` : ${detail}` : ''}`);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [fetchStatus]);

  const handleConnect = async (providerKey) => {
    setBusy((b) => ({ ...b, [providerKey]: true }));
    try {
      const res = await api.get(`/calendar/${providerKey}/initiate`);
      // Full-page redirect so OAuth callback returns cleanly to our backend
      window.location.href = res.data.authorization_url;
    } catch (err) {
      const detailMsg = err.response?.data?.detail || '';
      if (detailMsg.includes('credentials_not_configured')) {
        toast.error(
          `L'intégration ${providerKey === 'google' ? 'Google' : 'Outlook'} n'est pas encore configurée par l'administrateur (clés OAuth manquantes).`
        );
      } else {
        toast.error('Erreur lors de la connexion');
      }
      setBusy((b) => ({ ...b, [providerKey]: false }));
    }
  };

  const handleDisconnect = async (providerKey) => {
    if (!window.confirm(`Déconnecter ${providerKey === 'google' ? 'Google Calendar' : 'Microsoft Outlook'} ?`)) {
      return;
    }
    setBusy((b) => ({ ...b, [providerKey]: true }));
    try {
      await api.delete(`/calendar/${providerKey}`);
      toast.success('Calendrier déconnecté');
      await fetchStatus();
    } catch (err) {
      toast.error('Erreur lors de la déconnexion');
    } finally {
      setBusy((b) => ({ ...b, [providerKey]: false }));
    }
  };

  const handleChangeMode = async (providerKey, mode) => {
    try {
      await api.put(`/calendar/${providerKey}/mode`, { mode });
      toast.success('Mode de synchronisation mis à jour');
      await fetchStatus();
    } catch (err) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleSync = async (providerKey) => {
    setSyncing((s) => ({ ...s, [providerKey]: true }));
    try {
      const res = await api.post(`/calendar/${providerKey}/sync`, {
        days_back: 30,
        days_forward: 180,
      });
      const d = res.data;
      const parts = [];
      if (typeof d.imported === 'number') parts.push(`${d.imported} importés`);
      if (typeof d.updated === 'number') parts.push(`${d.updated} mis à jour`);
      if (typeof d.pushed === 'number') parts.push(`${d.pushed} poussés`);
      toast.success(parts.length ? `Sync : ${parts.join(', ')}` : 'Synchronisation effectuée');
      await fetchStatus();
    } catch (err) {
      const detailMsg = err.response?.data?.detail || '';
      if (detailMsg.includes('reauth_needed')) {
        toast.error('Reconnexion requise (token expiré)');
      } else if (detailMsg.includes('not_connected')) {
        toast.error('Compte non connecté');
      } else {
        toast.error('Erreur lors de la synchronisation');
      }
    } finally {
      setSyncing((s) => ({ ...s, [providerKey]: false }));
    }
  };

  if (loading) {
    return (
      <Card className="border border-slate-200">
        <CardContent className="py-12 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-[#065F46]" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border border-slate-200" data-testid="calendar-integrations-card">
      <CardHeader>
        <CardTitle className="font-outfit text-lg flex items-center gap-2">
          <CalendarIcon className="w-5 h-5 text-[#065F46]" />
          Calendriers électroniques
        </CardTitle>
        <CardDescription>
          Connectez vos comptes Google Calendar et Microsoft Outlook pour synchroniser vos rendez-vous dans les deux sens.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {PROVIDERS.map((p) => {
          const info = status.providers?.[p.key] || {};
          const configured = !!info.configured;
          const connected = !!info.connected;
          const mode = info.sync_mode || 'two_way';

          return (
            <div
              key={p.key}
              className={`rounded-lg border ${p.border} ${p.bg} p-4`}
              data-testid={`calendar-${p.key}-section`}
            >
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg bg-white flex items-center justify-center shadow-sm`}>
                    <CalendarIcon className={`w-5 h-5 ${p.accent}`} />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">{p.name}</p>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      {connected ? (
                        <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                          <CheckCircle2 className="w-3 h-3 mr-1" /> Connecté
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="bg-slate-50 text-slate-600">
                          Non connecté
                        </Badge>
                      )}
                      {!configured && (
                        <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                          <AlertCircle className="w-3 h-3 mr-1" /> Clés OAuth manquantes
                        </Badge>
                      )}
                      {connected && info.account_email && (
                        <span className="text-xs text-slate-500">{info.account_email}</span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {!connected && (
                    <Button
                      size="sm"
                      onClick={() => handleConnect(p.key)}
                      disabled={busy[p.key] || !configured}
                      className="bg-[#065F46] hover:bg-[#047857] text-white"
                      data-testid={`connect-${p.key}-btn`}
                    >
                      {busy[p.key] ? (
                        <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                      ) : (
                        <LinkIcon className="w-4 h-4 mr-1" />
                      )}
                      Connecter
                    </Button>
                  )}
                  {connected && (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleSync(p.key)}
                        disabled={syncing[p.key]}
                        data-testid={`sync-${p.key}-btn`}
                      >
                        {syncing[p.key] ? (
                          <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                        ) : (
                          <RefreshCw className="w-4 h-4 mr-1" />
                        )}
                        Synchroniser
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDisconnect(p.key)}
                        disabled={busy[p.key]}
                        data-testid={`disconnect-${p.key}-btn`}
                      >
                        <Unlink className="w-4 h-4 mr-1" />
                        Déconnecter
                      </Button>
                    </>
                  )}
                </div>
              </div>

              {connected && (
                <div className="mt-4 grid sm:grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Mode de synchronisation</p>
                    <Select value={mode} onValueChange={(v) => handleChangeMode(p.key, v)}>
                      <SelectTrigger className="w-full" data-testid={`sync-mode-${p.key}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {SYNC_MODE_OPTIONS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            <span className="flex items-center gap-2">
                              <opt.icon className="w-4 h-4" /> {opt.label}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {info.connected_at && (
                    <div>
                      <p className="text-xs text-slate-500 mb-1">Connecté depuis</p>
                      <p className="text-sm text-slate-700">
                        {new Date(info.connected_at).toLocaleString('fr-FR')}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}

        <p className="text-xs text-slate-500 mt-2">
          💡 La synchronisation bidirectionnelle pousse automatiquement vos nouveaux rendez-vous vers votre calendrier
          externe et importe les événements existants en sens inverse.
        </p>
      </CardContent>
    </Card>
  );
};

export default CalendarIntegrations;
